import enTranslations from '../translations/en.json';
import frTranslations from '../translations/fr.json';
import type { TranslationStructure, TranslationKey, SupportedLanguage } from '@/types/translations';

export type Language = SupportedLanguage;

// Type check that French translations match English structure
const _typeCheck: TranslationStructure = frTranslations;
// This will cause a TypeScript error if the French JSON doesn't match the English structure

const translations: Record<Language, TranslationStructure> = {
  en: enTranslations,
  fr: frTranslations,
};

/**
 * Get a nested value from an object using dot notation
 * Example: get(obj, 'notifications.title') returns obj.notifications.title
 */
function get(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => current?.[key], obj);
}

/**
 * Interpolate variables in a translation string
 * Example: interpolate("Hello {{name}}", { name: "John" }) returns "Hello John"
 */
function interpolate(template: string, variables: Record<string, any> = {}): string {
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return variables[key]?.toString() || match;
  });
}

/**
 * Get a translation for a given key and language
 * @param key - The translation key in dot notation (e.g., 'notifications.title')
 * @param language - The language code ('en' or 'fr')
 * @param variables - Variables to interpolate into the translation
 * @returns The translated string or the key if translation is not found
 */
export function getTranslation(
  key: TranslationKey,
  language: Language,
  variables?: Record<string, any>
): string {
  const translationData = translations[language];
  const translation = get(translationData, key);
  
  if (typeof translation !== 'string') {
    console.warn(`Translation not found for key: ${key} in language: ${language}`);
    return key;
  }
  
  return variables ? interpolate(translation, variables) : translation;
}

/**
 * Get all translations for a specific language
 * @param language - The language code
 * @returns The complete translation object for the language
 */
export function getLanguageTranslations(language: Language): TranslationStructure {
  return translations[language] || translations.en;
}

/**
 * Check if a translation key exists
 * @param key - The translation key to check
 * @param language - The language code
 * @returns True if the key exists, false otherwise
 */
export function hasTranslation(key: TranslationKey, language: Language): boolean {
  const translationData = translations[language];
  const translation = get(translationData, key);
  return translation !== undefined;
}

export { translations };

